import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { useAuth } from '@/hooks/use-auth';
import { useMounted } from '@/hooks/use-mounted';
import { useResponsive } from '@/hooks/use-responsive';
import { MultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator, getMultiFactorResolver } from 'firebase/auth';

import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import packageJson from '../../../package.json';
import useRefresh from '../../hooks/use-refresh';
import Button from '../basicComponents/button';
import Input from '../basicComponents/input';
import EnterCode from './modals/enter-code';

export const FirebaseLogin: FC = (props) => {
  const isMounted = useMounted();
  const navigate = useNavigate();
  const location = useLocation();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { signInWithEmailAndPassword, user, auth } = useAuth();

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'enter-code' });
  const [verificationId, setVerificationId] = useState<string>();
  const [multiFactorResolver, setMultiFactorResolver] = useState<MultiFactorResolver | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordMasked, setIsPasswordMasked] = useState(true);
  const { isMobile, isTablet, isPortrait } = useResponsive();

  const twoFactorAuthenticationMobileNumber = location?.state?.twoFactorAuthenticationMobileNumber;
  const changePasswordForLoggedInUser = location?.state?.changePasswordForLoggedInUser;
  const previousUserEmail = location?.state?.previousUserEmail;
  const returnUrl = location?.state?.returnUrl;

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };
  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      submit: null,
      isSubmitting: true
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
      password: Yup.string().required('Password is required')
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      setIsLoading(true);
      try {
        await signInWithEmailAndPassword(values.email, values.password);
      } catch (error: any) {
        if (error.code == 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(auth, error);
          setMultiFactorResolver(resolver);
          if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            const phoneInfoOptions = {
              multiFactorHint: resolver.hints[0],
              session: resolver.session
            };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            try {
              const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions);
              setVerificationId(verificationId);
              openModalWindow('enter-code');
            } catch (error) {
              console.log(error);
            }
          } else {
            if (isMounted()) {
              helpers.setStatus({ success: false });
              helpers.setErrors({
                password: 'We do not recognize your login credentials'
              });
              helpers.setSubmitting(true);
            }
          }
        } else if (error.code == 'auth/wrong-password' || error.code == 'auth/user-not-found' || error.code == 'auth/user-disabled') {
          if (isMounted()) {
            helpers.setStatus({ success: false });
            helpers.setErrors({
              email: ' ',
              password: 'We do not recognize your login credentials'
            });
            helpers.setSubmitting(true);
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  });

  useEffect(() => {
    if (user && user.tenant) {
      if (twoFactorAuthenticationMobileNumber && previousUserEmail === user.email) {
        navigate('/two-factor-authentication', {
          state: {
            twoFactorAuthenticationMobileNumber
          }
        });
        return;
      }
      if (changePasswordForLoggedInUser) {
        navigate('/change-password');
        return;
      }

      const path = user.tenant.type === TenantType.ADVISORY_FIRM ? '/dealCatalog' : '/dealPipeline';
      navigate(returnUrl ? returnUrl : path, { state: { isFirstOpen: true } });
    }
  }, [user, twoFactorAuthenticationMobileNumber, previousUserEmail]);

  const verify2FACode = async (verificationCode: string) => {
    if (!verificationCode || !verificationId || !multiFactorResolver) {
      throw new Error(defaultErrorMessage);
    }

    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactorResolver.resolveSignIn(multiFactorAssertion);
  };

  useRefresh();

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'enter-code' && (
        <EnterCode isOpen={modalWindow.isOpen} verify2FACode={verify2FACode} onClose={closeModalWindow} />
      )}
      <LoginWrapper isMobile={isMobile} isPortrait={isPortrait} isTablet={isTablet}>
        <LoginForm noValidate onSubmit={formik.handleSubmit} {...props}>
          <Input
            label="Enter Your Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            styleType={formik.touched.email && formik.errors.email ? 'error' : 'success'}
            errorText={formik.touched.email && formik.errors.email}
            type="email"
            name="email"
            size="lg"
            isRequiredField
          />
          <Input
            label="Enter Your Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            styleType={formik.touched.password && formik.errors.password ? 'error' : 'success'}
            errorText={formik.touched.password && formik.errors.password}
            type="password"
            name="password"
            size="lg"
            isRequiredField
            withPasswordIcon
          />
          <SignInButtonWrapper>
            <Button
              id="sign-in-button"
              styleType={formik.isSubmitting || !formik.isValid || isLoading ? 'disabled' : 'default'}
              isLoading={isLoading}
              type="submit"
              size="lg"
              width="100%"
            >
              Sign In
            </Button>
          </SignInButtonWrapper>
          <StyledLink to="/password-recovery">Forgot Password</StyledLink>
        </LoginForm>
      </LoginWrapper>
      {packageJson.version}
    </>
  );
};

const LoginWrapper = styled.div<{ isMobile: boolean; isPortrait: boolean; isTablet: boolean }>`
  color: #373f4e;
  font-family: 'Blinker', serif;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  width: ${({ isMobile, isPortrait, isTablet }) => (isMobile || (isPortrait && isTablet) ? '70%' : '100%')};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SignInButtonWrapper = styled.div`
  margin-top: 10px;

  .grecaptcha-badge {
    visibility: hidden !important;
  }
`;

export const StyledLink = styled(Link)`
  display: flex;
  justify-content: center;
  font-weight: 600;
  text-decoration: none;
  color: ${themeVar('font.action')};
`;
